import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { services } from "@/mocks/homeData";

export default function Services() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const isRTL = i18n.language?.startsWith("ar");

  const handleCardClick = () => {
    navigate("/services");
  };

  return (
    <section id="services" className="w-full py-16 md:py-24 bg-[#F5F3EF]">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          className="max-w-4xl mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 border border-brand/20 text-brand px-4 py-2 text-xs md:text-sm font-semibold mb-4">
            <i className="ri-stack-line" />
            {t("services.homeProductsEyebrow")}
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-stone-900 leading-[1.22] mb-5">
            {t("services.homeProductsTitle")}
          </h2>
          <p className="text-base md:text-lg text-stone-600 leading-relaxed max-w-3xl">
            {t("services.homeProductsDesc")}
          </p>
        </motion.div>

        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className="group flex flex-col rounded-2xl border border-stone-200 bg-white overflow-hidden cursor-pointer shadow-[0_10px_35px_rgba(0,0,0,0.04)]"
              initial={{ opacity: 0, y: 60 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
              transition={{ duration: 0.6, delay: 0.15 * index, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={handleCardClick}
            >
              <div className="p-6 md:p-8 pb-5">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <span className="inline-flex items-center rounded-full bg-stone-100 text-stone-700 px-3 py-1 text-xs md:text-sm font-semibold">
                    {t("services.homeProductLabel")} {`0${index + 1}`}
                  </span>
                  <span className="text-xs md:text-sm font-medium text-brand">
                    {index === 0 ? t("services.warehouseTitle") : t("services.supermarketTitle")}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3 leading-tight">
                  {index === 0 ? t("services.warehouseTitle") : t("services.supermarketTitle")}
                </h3>
                <p className="text-sm md:text-base text-stone-600 leading-relaxed">
                  {index === 0 ? t("services.warehouseDesc") : t("services.supermarketDesc")}
                </p>
              </div>

              <motion.div
                className={`mx-6 md:mx-8 rounded-2xl border border-stone-200 p-4 md:p-5 ${
                  index === 0
                    ? "bg-gradient-to-br from-brand/15 via-brand/5 to-white"
                    : "bg-gradient-to-br from-amber-200/35 via-amber-100/30 to-white"
                }`}
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.3 }}
              >
                <div className="h-56 md:h-64 rounded-xl bg-white/80 flex items-center justify-center overflow-hidden">
                  <img
                    src={service.image}
                    alt={index === 0 ? t("services.warehouseTitle") : t("services.supermarketTitle")}
                    className="w-full h-full object-contain"
                  />
                </div>
              </motion.div>

              <div className="px-6 md:px-8 py-6 mt-auto flex items-center justify-between border-t border-stone-100">
                <span className="text-sm md:text-base font-medium text-stone-700">
                  {t("services.homeProductCta")}
                </span>
                <span className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                  <i className={`ri-${isRTL ? "arrow-left-line" : "arrow-right-line"} text-sm`} />
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}